import { isMobile } from 'react-device-detect';
import styled from 'styled-components';
import { NavLink } from 'react-router-dom';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { TeamsContext } from 'contexts/teams';
import Avatar from './Avatar';
import Active from './Active';
import Manager from './Manager';
import Pending from './Pending';
import Checkbox from 'components/checkbox';
import Tooltip from 'components/tooltip';
import { Cell, Pagination, Row, SortableHeader, Table, Top } from 'components/Table';
import { fromOrdering, getColumnDirection, getNextSortState, toOrdering } from 'utils/sorting';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { screenMd } from 'constants/styles/breakpoints';
import { ContentAccessMode, Team } from 'contexts/teams/types';
import SearchEmptyState from 'components/searchEmptyState';
import { HeaderLoading } from 'components/loadings';
import TeamsSettings from './TeamsSettings';
import { SessionContext } from 'contexts/session';
import useManageTeam from '../hooks/useManageTeam';
import useInviteToTeam from '../hooks/useInviteToTeam';
import ButtonIcon from 'components/buttonIcon';
import { ActionButtonsContainer } from 'components/usersTable/styles';
import Icon from 'components/icons';
import SortTable from 'components/sortTable';
import TeamContentAccessDropdown from './TeamContentAccessDropdown';
import { WRAPPING_TOOLTIP_STYLE } from 'constants/components/tooltip';

export const Container = styled.div<{ lock: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: 140px;

  ${screenMd} {
    padding-bottom: 0;
  }

  ${({ lock }) =>
    lock &&
    `
		opacity: 0.4;
		pointer-events: none;
	`}
`;

const ContentAccessHeader = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const contentAccessTooltipStyle = {
  background: 'var(--light-gray-darker)',
  color: 'var(--white)',
  borderRadius: '4px',
  padding: '8px 16px',
  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
  width: '257px',
  textAlign: 'center' as const,
  fontSize: '14px',
  lineHeight: '20px',
};

const isTeamManageableByManager = (teamId: string, sessionTeams: Team[] = []): boolean => {
  const managedTeam = sessionTeams.find((team) => team?.id === teamId || team?.team_id === teamId);
  return String(managedTeam?.is_manager) === 'true';
};

const TeamsTable = ({ lock }: { lock: boolean }): JSX.Element => {
  const {
    table,
    actions: teamsTableActions,
    selectedItems,
    isEmpty,
    fetching,
    filters: { page_size, page, ordering },
  } = useContext(TeamsContext);
  const sortState = fromOrdering(ordering || null);
  const {
    teams,
    user,
    actions: sessionActions,
    account,
    licenses_available,
    isAccountOwner,
    isTeamManager,
    isAccountContentRestrictionsEnabled,
  } = useContext(SessionContext);
  const { dashboardUsersSorting, contentRestrictions } = useFlags() || {};

  useEffect(() => {
    if (!dashboardUsersSorting && ordering) {
      teamsTableActions.onChangeFilters({ name: 'ordering', value: '' });
    }
  }, [dashboardUsersSorting]);

  const { manageTeamModal, openManageTeamModal, closeManageTeamModal } = useManageTeam();
  const { inviteToTeamModal, openInviteToTeamModal, closeInviteToTeamModal } = useInviteToTeam();
  const countRows = selectedItems?.length || 0;
  const selectedAllRows = Boolean(countRows > 0 && selectedItems?.length === table?.items?.length);
  const intermediateSelection = Boolean(
    countRows > 0 && selectedItems?.length !== table?.items?.length
  );

  const isContentRestrictionsVisible =
    Boolean(contentRestrictions) && Boolean(isAccountContentRestrictionsEnabled);

  const canManageTeam = useCallback(
    (team: Team): boolean => {
      if (isAccountOwner) {
        return true;
      }

      const teamId = team?.id || team?.team_id;

      if (!isTeamManager || !teamId) {
        return false;
      }

      return isTeamManageableByManager(teamId, user?.teams ?? teams ?? []);
    },
    [isAccountOwner, isTeamManager, teams, user?.teams]
  );

  const hasManageableTeam = useMemo(() => {
    return Boolean(table?.items?.some((team) => canManageTeam(team)));
  }, [table?.items, canManageTeam]);

  const showContentAccessColumn = Boolean(isContentRestrictionsVisible && hasManageableTeam);

  const handleSortToggle = (columnKey: string) => {
    const nextState = getNextSortState(sortState, columnKey);
    const nextOrdering = toOrdering(nextState);
    teamsTableActions.onChangeFilters({ name: 'ordering', value: nextOrdering || '' });
  };
  const sortableColumns = [
    { label: 'Team', value: 'name' },
    { label: 'Active users', value: 'active_users' },
    { label: 'Pending users', value: 'pending_users' },
    { label: 'Manager(s)', value: 'manager_display' },
  ];

  const onAddUser = (team: Team) => {
    onInviteToTeam(team);
  };

  const onEditTeam = (team: Team) => {
    const handleEditFinish = (data) => {
      if (data) {
        sessionActions.updateAccountTeams((prev) => {
          const editedIndex = prev.findIndex(({ id }) => id === data.id);
          prev[editedIndex] = data;
          return [...prev];
        });
        teamsTableActions.onForceTeamsRequest();
      }
      closeManageTeamModal();
    };

    openManageTeamModal({
      onClose: handleEditFinish,
      team: team,
      existingTeams: teams,
    });
  };

  const onInviteToTeam = (team: Team) => {
    const handleInviteFinish = (afterInvite) => {
      if (afterInvite) {
        if (!licenses_available) {
          sessionActions.refreshSession();
        }
        teamsTableActions.onForceTeamsRequest();
      }
      closeInviteToTeamModal();
    };

    openInviteToTeamModal({
      onClose: handleInviteFinish,
      team: team,
      account: account,
      isInvite: true,
    });
  };

  const deleteDisabled = (item: Team) => {
    return item.active_users_count > 0 || item.pending_users_count > 0;
  };

  const handleContentAccessSaved = (teamId: string, contentAccessMode: ContentAccessMode) => {
    const rows = table.items || [];
    teamsTableActions.onTableUpdate(
      rows.map((item) =>
        item.id === teamId || item.team_id === teamId
          ? { ...item, content_access_mode: contentAccessMode }
          : item
      )
    );
  };

  const teamsColumnsTableConfig = isMobile
    ? '40px 6fr 40px'
    : showContentAccessColumn
    ? '40px minmax(220px, 5fr) 2fr 2fr 2fr 263px 150px'
    : '40px 6fr 3fr 3fr 3fr 150px';

  return (
    <Container lock={lock}>
      {!isEmpty ? (
        <Table>
          <Row
            columns={teamsColumnsTableConfig}
            transparent={isMobile}
            topSelect={isMobile}
            top={true}
            sticky
          >
            <Top
              topSelect={isMobile}
              style={!isMobile ? { justifyContent: 'flex-start', paddingLeft: 0 } : {}}
            >
              <Checkbox
                checked={selectedAllRows}
                indeterminate={intermediateSelection}
                onChange={() => teamsTableActions?.onSelectAllRows()}
                label={isMobile ? `${countRows} teams selected` : undefined}
                darkBackground={isMobile}
                id="allTeamSelected"
                controlled
                style={
                  isMobile
                    ? { justifyContent: 'flex-start', margin: '0 0 4px 8px', opacity: 0.6 }
                    : {}
                }
              />
              {isMobile && dashboardUsersSorting && (
                <SortTable
                  title="Sort by"
                  name="ordering"
                  position="left"
                  items={[
                    { label: 'Clear sort', value: '', selected: !ordering },
                    ...sortableColumns.flatMap((column) => [
                      {
                        label: `${column.label} ASC`,
                        value: column.value,
                        selected: ordering === column.value,
                      },
                      {
                        label: `${column.label} DESC`,
                        value: `-${column.value}`,
                        selected: ordering === `-${column.value}`,
                      },
                    ]),
                  ]}
                  onSelect={teamsTableActions.onChangeFilters}
                />
              )}
            </Top>
            {!isMobile && (
              <>
                <Top>
                  {dashboardUsersSorting ? (
                    <SortableHeader
                      label="Team"
                      columnKey="name"
                      direction={getColumnDirection(sortState, 'name')}
                      onToggle={handleSortToggle}
                    />
                  ) : (
                    <span>Team</span>
                  )}
                </Top>
                <Top>
                  {dashboardUsersSorting ? (
                    <SortableHeader
                      label="Active users"
                      columnKey="active_users"
                      direction={getColumnDirection(sortState, 'active_users')}
                      onToggle={handleSortToggle}
                      tooltip="Number of users who have accepted the invite and are now part of the team"
                    />
                  ) : (
                    <>
                      <span>Active users</span>
                      <Tooltip
                        title="Number of users who have accepted the invite and are now part of the team"
                        placement="right"
                        overlayInnerStyle={WRAPPING_TOOLTIP_STYLE}
                      >
                        <Icon
                          data-testid="info-inactive-icon"
                          icon="Info"
                          fill="var(--lilac)"
                          size="small"
                          style={{ marginLeft: '5px', width: '14px', height: '14px' }}
                        />
                      </Tooltip>
                    </>
                  )}
                </Top>
                <Top>
                  {dashboardUsersSorting ? (
                    <SortableHeader
                      label="Pending users"
                      columnKey="pending_users"
                      direction={getColumnDirection(sortState, 'pending_users')}
                      onToggle={handleSortToggle}
                      tooltip="Number of users who have been invited to the team but have not yet accepted the invite"
                    />
                  ) : (
                    <>
                      <span>Pending users</span>
                      <Tooltip
                        title="Number of users who have been invited to the team but have not yet accepted the invite"
                        placement="right"
                        overlayInnerStyle={WRAPPING_TOOLTIP_STYLE}
                      >
                        <Icon
                          data-testid="info-inactive-icon"
                          icon="Info"
                          fill="var(--lilac)"
                          size="small"
                          style={{ marginLeft: '5px', width: '14px', height: '14px' }}
                        />
                      </Tooltip>
                    </>
                  )}
                </Top>
                <Top>
                  {dashboardUsersSorting ? (
                    <SortableHeader
                      label="Manager(s)"
                      columnKey="manager_display"
                      direction={getColumnDirection(sortState, 'manager_display')}
                      onToggle={handleSortToggle}
                    />
                  ) : (
                    <span>Manager(s)</span>
                  )}
                </Top>
                {showContentAccessColumn && (
                  <Top>
                    <ContentAccessHeader>
                      <span>Learner content access</span>
                      <Tooltip
                        title="Controls learner access only. Team Managers and Account Owners aren't affected by this setting."
                        placement="top"
                        overlayInnerStyle={contentAccessTooltipStyle}
                      >
                        <Icon icon="Info" fill="#A793F6" size="small" />
                      </Tooltip>
                    </ContentAccessHeader>
                  </Top>
                )}
                <Top></Top>
              </>
            )}
          </Row>

          {table?.items?.map((item, index) => (
            <Row columns={teamsColumnsTableConfig} key={index}>
              <Cell style={{ justifyContent: 'flex-start', paddingLeft: 0 }}>
                <Checkbox
                  checked={Boolean(selectedItems?.includes(item.id))}
                  onChange={() => teamsTableActions?.onSelectRow(item.id)}
                  controlled
                />
              </Cell>
              <Cell>
                <NavLink to={`/team/${item.id}`}>
                  <Avatar data={item} />
                </NavLink>
              </Cell>
              {!isMobile && (
                <>
                  <Cell>{item.active_users && <Active data={item.active_users} />}</Cell>
                  <Cell>{item.pending_users && <Pending data={item.pending_users} />}</Cell>
                  <Cell>{item.team_managers && <Manager data={item.team_managers} />}</Cell>
                  {showContentAccessColumn && (
                    <Cell>
                      <TeamContentAccessDropdown
                        team={item}
                        canManage={canManageTeam(item)}
                        onSaved={handleContentAccessSaved}
                      />
                    </Cell>
                  )}
                  <Cell style={{ display: 'block', minWidth: '140px', textAlign: 'right' }}>
                    <ActionButtonsContainer>
                      <Tooltip placement="bottomRight" title={'Edit team'}>
                        <ButtonIcon
                          $secondary
                          style={{ margin: '0px 5px' }}
                          size={'small'}
                          icon={'Edit'}
                          onClick={() => onEditTeam(item)}
                        />
                      </Tooltip>
                      <Tooltip placement="bottomRight" title={'Add user'}>
                        <ButtonIcon
                          style={{ margin: '0px 5px' }}
                          $secondary
                          size={'small'}
                          icon={'UserPlus'}
                          onClick={() => onAddUser(item)}
                        />
                      </Tooltip>
                      <Tooltip
                        placement="bottomRight"
                        overlayInnerStyle={{ maxWidth: 250 }}
                        title={
                          deleteDisabled(item)
                            ? 'Teams can only be deleted by removing the users first. Remove the users, then delete the team.'
                            : 'Remove team'
                        }
                      >
                        <div
                          style={{
                            cursor: deleteDisabled(item) ? 'not-allowed' : 'pointer',
                          }}
                        >
                          <ButtonIcon
                            style={{ margin: '0px 5px' }}
                            $secondary
                            disabled={deleteDisabled(item)}
                            size={'small'}
                            icon={'Trash'}
                            onClick={() => teamsTableActions.onRemoveTeam([item.id])}
                          />
                        </div>
                      </Tooltip>
                    </ActionButtonsContainer>
                  </Cell>
                </>
              )}
              {isMobile && (
                <TeamsSettings data={item} onRemoveTeam={teamsTableActions.onRemoveTeam} />
              )}
            </Row>
          ))}
        </Table>
      ) : (
        <>{!fetching && <SearchEmptyState />}</>
      )}
      {table.items && !isEmpty && (
        <Pagination
          count={table.count}
          page={parseInt(page)}
          page_size={parseInt(page_size)}
          onPagesClick={(value, name) =>
            teamsTableActions && teamsTableActions.onChangeFilters({ name: name, value })
          }
        />
      )}

      {manageTeamModal}
      {inviteToTeamModal}
      <HeaderLoading portals={true} active={fetching} />
    </Container>
  );
};

export default TeamsTable;
